/**
 * HLS 视频分片缓存（阿里云盘视频专用，路径 B 方案）
 *
 * 设计要点：
 * - 阿里云盘 HLS 分片直链形如 .../seg0.ts?x-oss-signature=...&x-oss-expires=...
 *   其中「路径部分」（pathname）稳定不变，只有签名查询参数会过期。
 *   因此用「归一化后的分片路径」作为缓存键，签名过期不影响命中。
 * - 播放时由自定义 hls.js loader 拦截分片请求：命中缓存→直接返回字节(0 流量)，
 *   未命中→走 CDN 并顺手写入缓存（边播边存，播多少存多少）。
 * - m3u8 清单不缓存：每次播放从阿里云预览 CDN 重新获取（不占下载次数限制）。
 * - 完整性判定：解析某清晰度档的 level m3u8 得到该档全部分片路径，存为 manifest；
 *   当该档所有分片都在缓存中时，该视频才标记「已缓存」。
 *
 * 存储：复用 music-cache-db（与音频/视频/封面共库），新增对象存储：
 * - video-segments：分片字节，key = segmentCacheKey
 * - video-seg-manifest：每个视频每档的分片清单，key = manifestKey
 */

const DB_NAME = 'music-cache-db';
const DB_VERSION = 9; // 🔥 升级到 9：新增 audio-ranges / audio-metadata（音频 Range 缓存）
const SEGMENT_STORE = 'video-segments';
const MANIFEST_STORE = 'video-seg-manifest';
const META_STORE = 'meta';

// 分片缓存 LRU 上限（与音频 500MB 分开计量，视频分片体积大，给 1GB）
const MAX_SEGMENT_CACHE_SIZE = 1024 * 1024 * 1024;
const SEGMENT_META_KEY = 'video-segments-lru';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      // 兼容历史对象存储：升级事务里只新增，不动已有
      if (!db.objectStoreNames.contains('music-files')) db.createObjectStore('music-files');
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
      if (!db.objectStoreNames.contains('video-files')) db.createObjectStore('video-files');
      if (!db.objectStoreNames.contains('partial-video-cache')) db.createObjectStore('partial-video-cache');
      if (!db.objectStoreNames.contains('music-album')) db.createObjectStore('music-album');
      if (!db.objectStoreNames.contains('music-lrc')) db.createObjectStore('music-lrc');
      // 🔥 新增分片相关对象存储
      if (!db.objectStoreNames.contains(SEGMENT_STORE)) {
        db.createObjectStore(SEGMENT_STORE);
        console.log('🎞️ [分片缓存] 创建 video-segments 对象存储');
      }
      if (!db.objectStoreNames.contains(MANIFEST_STORE)) {
        db.createObjectStore(MANIFEST_STORE);
        console.log('🎞️ [分片缓存] 创建 video-seg-manifest 对象存储');
      }
      // 🔥 v9 新增：音频 Range 缓存（in-line key）
      if (!db.objectStoreNames.contains('audio-ranges')) {
        const rs = db.createObjectStore('audio-ranges', { keyPath: 'key' });
        rs.createIndex('audioId', 'audioId', { unique: false });
        rs.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
      if (!db.objectStoreNames.contains('audio-metadata')) {
        db.createObjectStore('audio-metadata', { keyPath: 'audioId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

/**
 * 归一化分片/清单 URL → 稳定缓存键。
 * 去掉会变化的 OSS 签名查询参数（x-oss-*、Expires、Signature 等），只保留 host+pathname。
 * 对 blob:/data: 等非 http 链接返回 null（不缓存）。
 * @param {string} url
 * @returns {string|null}
 */
export function normalizeSegmentUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (!/^https?:\/\//i.test(url)) return null;
  try {
    const u = new URL(url);
    // host + pathname 即为稳定标识；阿里云转码产物 object path 固定，仅签名参数变化
    return `${u.host}${u.pathname}`;
  } catch (_) {
    // URL 解析失败时退化为「去掉 ? 之后」的部分
    const qIndex = url.indexOf('?');
    return qIndex >= 0 ? url.slice(0, qIndex) : url;
  }
}

/**
 * 将 m3u8 内相对分片路径改写为绝对 https URL。
 * blob manifest 起播时，相对路径会被解析成 blob:.../media.ts，导致分片无法命中 IndexedDB 缓存。
 * @param {string} playlistText
 * @param {string} levelKey normalizeSegmentUrl 形式（host/path/media.m3u8）
 * @returns {string}
 */
export function rewritePlaylistWithAbsoluteUrls(playlistText, levelKey) {
  if (!playlistText || !levelKey) return playlistText;
  let baseDir;
  try {
    baseDir = new URL('.', `https://${levelKey}`).href;
  } catch (_) {
    return playlistText;
  }
  return playlistText.split(/\r?\n/).map((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return line;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    try {
      return new URL(trimmed, baseDir).href;
    } catch (_) {
      return line;
    }
  }).join('\n');
}

/**
 * 构建分片缓存键：videoId 维度隔离 + 归一化路径。
 * 不带 userId：同一视频文件分片内容与用户无关，跨用户可共享（视频访问权限在取直链时已校验）。
 * @param {string|number} videoId
 * @param {string} normalizedPath
 * @returns {string}
 */
export function buildSegmentKey(videoId, normalizedPath) {
  return `v${videoId}::${normalizedPath}`;
}

/** 构建某清晰度档的清单键 */
export function buildManifestKey(videoId, levelKey) {
  return `v${videoId}::level::${levelKey}`;
}

function txStore(db, storeName, mode) {
  const tx = db.transaction(storeName, mode);
  return tx.objectStore(storeName);
}

async function getMeta(db) {
  return new Promise((resolve) => {
    try {
      const store = txStore(db, META_STORE, 'readonly');
      const req = store.get(SEGMENT_META_KEY);
      req.onsuccess = () => resolve(req.result || { order: [], size: 0, map: {} });
      req.onerror = () => resolve({ order: [], size: 0, map: {} });
    } catch (_) {
      resolve({ order: [], size: 0, map: {} });
    }
  });
}

async function setMeta(db, meta) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(META_STORE, 'readwrite');
      tx.objectStore(META_STORE).put(meta, SEGMENT_META_KEY);
      tx.oncomplete = resolve;
      tx.onerror = () => resolve();
    } catch (_) {
      resolve();
    }
  });
}

/**
 * 查询某分片是否已缓存，命中返回 ArrayBuffer，否则返回 null。
 * @param {string} segmentKey
 * @returns {Promise<ArrayBuffer|null>}
 */
export async function getSegment(segmentKey) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      try {
        const store = txStore(db, SEGMENT_STORE, 'readonly');
        const req = store.get(segmentKey);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      } catch (_) {
        resolve(null);
      }
    });
  } catch (_) {
    return null;
  }
}

/** 批量判断分片是否存在（用于完整性判定，避免逐个开事务） */
export async function hasSegments(segmentKeys) {
  if (!Array.isArray(segmentKeys) || segmentKeys.length === 0) return [];
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const result = new Array(segmentKeys.length).fill(false);
      let remaining = segmentKeys.length;
      let store;
      try {
        store = txStore(db, SEGMENT_STORE, 'readonly');
      } catch (_) {
        resolve(result);
        return;
      }
      segmentKeys.forEach((key, idx) => {
        const req = store.getKey ? store.getKey(key) : store.get(key);
        req.onsuccess = () => {
          result[idx] = req.result !== undefined && req.result !== null;
          if (--remaining === 0) resolve(result);
        };
        req.onerror = () => {
          if (--remaining === 0) resolve(result);
        };
      });
    });
  } catch (_) {
    return new Array(segmentKeys.length).fill(false);
  }
}

/**
 * 写入分片字节（含 LRU 淘汰）。
 * @param {string} segmentKey
 * @param {ArrayBuffer} buffer
 */
export async function putSegment(segmentKey, buffer) {
  if (!segmentKey || !buffer) return;
  const size = buffer.byteLength || 0;
  if (size <= 0) return;
  try {
    const db = await openDB();
    const meta = await getMeta(db);

    // 已存在则先移除旧记录，避免重复计量
    if (meta.map[segmentKey]) {
      const idx = meta.order.indexOf(segmentKey);
      if (idx !== -1) meta.order.splice(idx, 1);
      meta.size -= meta.map[segmentKey];
      delete meta.map[segmentKey];
    }

    // LRU 淘汰直到有足够空间
    while (meta.size + size > MAX_SEGMENT_CACHE_SIZE && meta.order.length > 0) {
      const oldest = meta.order.shift();
      if (oldest === undefined) break;
      await deleteSegmentRaw(db, oldest);
      if (meta.map[oldest]) {
        meta.size -= meta.map[oldest];
        delete meta.map[oldest];
      }
    }

    await new Promise((resolve) => {
      try {
        const tx = db.transaction(SEGMENT_STORE, 'readwrite');
        tx.objectStore(SEGMENT_STORE).put(buffer, segmentKey);
        tx.oncomplete = resolve;
        tx.onerror = () => resolve();
      } catch (_) {
        resolve();
      }
    });

    meta.order.push(segmentKey);
    meta.map[segmentKey] = size;
    meta.size += size;
    await setMeta(db, meta);
  } catch (e) {
    console.warn('🎞️ [分片缓存] 写入分片失败:', e);
  }
}

async function deleteSegmentRaw(db, segmentKey) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(SEGMENT_STORE, 'readwrite');
      tx.objectStore(SEGMENT_STORE).delete(segmentKey);
      tx.oncomplete = resolve;
      tx.onerror = () => resolve();
    } catch (_) {
      resolve();
    }
  });
}

/**
 * 保存某清晰度档的分片清单（用于完整性判定）。
 * @param {string} manifestKey
 * @param {{videoId:(string|number), levelKey:string, segmentKeys:string[], total:number, updatedAt:number}} manifest
 */
export async function putManifest(manifestKey, manifest) {
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      try {
        const tx = db.transaction(MANIFEST_STORE, 'readwrite');
        tx.objectStore(MANIFEST_STORE).put(manifest, manifestKey);
        tx.oncomplete = resolve;
        tx.onerror = () => resolve();
      } catch (_) {
        resolve();
      }
    });
  } catch (e) {
    console.warn('🎞️ [分片缓存] 写入清单失败:', e);
  }
}

/** 读取某清晰度档的分片清单 */
export async function getManifest(manifestKey) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      try {
        const store = txStore(db, MANIFEST_STORE, 'readonly');
        const req = store.get(manifestKey);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      } catch (_) {
        resolve(null);
      }
    });
  } catch (_) {
    return null;
  }
}

/** 列出某视频的所有清晰度档清单 */
export async function getManifestsForVideo(videoId) {
  try {
    const db = await openDB();
    const prefix = `v${videoId}::level::`;
    return await new Promise((resolve) => {
      const list = [];
      try {
        const store = txStore(db, MANIFEST_STORE, 'readonly');
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) {
            resolve(list);
            return;
          }
          if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) {
            list.push(cursor.value);
          }
          cursor.continue();
        };
        req.onerror = () => resolve(list);
      } catch (_) {
        resolve(list);
      }
    });
  } catch (_) {
    return [];
  }
}

/**
 * 判定视频是否「已完整缓存」：任一清晰度档的全部分片都在缓存中即算完整。
 * 用户播过的那一档播放完整即满足，符合「看到什么缓存什么」+「整档齐了才标已缓存」。
 * @param {string|number} videoId
 * @returns {Promise<boolean>}
 */
export async function isVideoFullyCached(videoId) {
  return !!(await getFullyCachedLevelKey(videoId));
}

/**
 * 返回已完整缓存的最高清晰度档 levelKey（normalizeSegmentUrl 形式），未完整缓存则 null。
 * 多档均完整时取 pathname 字典序最大（通常对应最高清 m3u8 路径）。
 */
export async function getFullyCachedLevelKey(videoId) {
  const manifests = await getManifestsForVideo(videoId);
  if (!manifests.length) return null;
  let bestKey = null;
  for (const m of manifests) {
    if (!m || !Array.isArray(m.segmentKeys) || m.segmentKeys.length === 0) continue;
    const flags = await hasSegments(m.segmentKeys);
    if (flags.length && flags.every(Boolean)) {
      const key = m.levelKey || '';
      if (!bestKey || key.localeCompare(bestKey) > 0) {
        bestKey = key;
      }
    }
  }
  return bestKey;
}

/** 删除某视频的所有分片与清单（清理缓存用） */
export async function deleteVideoSegments(videoId) {
  try {
    const db = await openDB();
    const segPrefix = `v${videoId}::`;
    const meta = await getMeta(db);

    await new Promise((resolve) => {
      try {
        const tx = db.transaction(SEGMENT_STORE, 'readwrite');
        const store = tx.objectStore(SEGMENT_STORE);
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return;
          if (typeof cursor.key === 'string'
              && cursor.key.startsWith(segPrefix)
              && !cursor.key.includes('::level::')) {
            // 更新 LRU 计量
            if (meta.map[cursor.key]) {
              const idx = meta.order.indexOf(cursor.key);
              if (idx !== -1) meta.order.splice(idx, 1);
              meta.size -= meta.map[cursor.key];
              delete meta.map[cursor.key];
            }
            cursor.delete();
          }
          cursor.continue();
        };
        tx.oncomplete = resolve;
        tx.onerror = () => resolve();
      } catch (_) {
        resolve();
      }
    });

    await setMeta(db, meta);

    await new Promise((resolve) => {
      try {
        const tx = db.transaction(MANIFEST_STORE, 'readwrite');
        const store = tx.objectStore(MANIFEST_STORE);
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return;
          if (typeof cursor.key === 'string' && cursor.key.startsWith(segPrefix)) {
            cursor.delete();
          }
          cursor.continue();
        };
        tx.oncomplete = resolve;
        tx.onerror = () => resolve();
      } catch (_) {
        resolve();
      }
    });
  } catch (e) {
    console.warn('🎞️ [分片缓存] 删除视频分片失败:', e);
  }
}

/** 当前分片缓存占用字节数 */
export async function getSegmentCacheSize() {
  try {
    const db = await openDB();
    const meta = await getMeta(db);
    return meta.size || 0;
  } catch (_) {
    return 0;
  }
}

/**
 * 批量获取「分片完整缓存」的视频维度 id 集合（用于列表批量标记已缓存）。
 * 返回的 id 即 buildManifestKey 用的 videoId（如单曲视频为 "m{songId}"，图集视频为 videoId）。
 * @returns {Promise<Set<string>>}
 */
export async function getFullyCachedVideoIds() {
  const result = new Set();
  try {
    const db = await openDB();
    // 读取全部清单，按 videoId 分组
    const manifests = await new Promise((resolve) => {
      const list = [];
      try {
        const store = txStore(db, MANIFEST_STORE, 'readonly');
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) { resolve(list); return; }
          if (cursor.value) list.push(cursor.value);
          cursor.continue();
        };
        req.onerror = () => resolve(list);
      } catch (_) {
        resolve(list);
      }
    });
    if (!manifests.length) return result;

    // 收集所有需要校验的分片键，一次性查询存在性
    const allKeys = [];
    for (const m of manifests) {
      if (m && Array.isArray(m.segmentKeys)) allKeys.push(...m.segmentKeys);
    }
    const flagList = await hasSegments(allKeys);
    const existSet = new Set();
    allKeys.forEach((k, i) => { if (flagList[i]) existSet.add(k); });

    // 任一档全分片齐全则该视频算已缓存
    for (const m of manifests) {
      if (result.has(m.videoId)) continue;
      if (!m || !Array.isArray(m.segmentKeys) || m.segmentKeys.length === 0) continue;
      if (m.segmentKeys.every((k) => existSet.has(k))) {
        result.add(m.videoId);
      }
    }
  } catch (e) {
    console.warn('🎞️ [分片缓存] 批量完整性查询失败:', e);
  }
  return result;
}

/**
 * 缓存 level m3u8 清单内容（用于离线重播，免网络请求）。
 * @param {string} videoId 视频维度 ID（如 m3067）
 * @param {string} levelKey 档位标识（如 FHD、HD，用于区分多档）
 * @param {string} content m3u8 文本内容
 */
export async function putPlaylist(videoId, levelKey, content) {
  if (!videoId || !levelKey || typeof content !== 'string') return;
  try {
    const db = await openDB();
    const key = `${videoId}::level::${levelKey}`;
    const store = txStore(db, MANIFEST_STORE, 'readwrite');
    store.put({ key, videoId, levelKey, content, cachedAt: Date.now() }, key);
    console.log(`🎞️ [分片缓存] 已缓存 level m3u8: ${key}`);
  } catch (e) {
    console.warn('🎞️ [分片缓存] 缓存 m3u8 失败:', e);
  }
}

/**
 * 读取缓存的 level m3u8 清单。
 * @param {string} videoId
 * @param {string} levelKey
 * @returns {Promise<string|null>} m3u8 文本内容，未命中返回 null
 */
export async function getPlaylist(videoId, levelKey) {
  if (!videoId || !levelKey) return null;
  try {
    const db = await openDB();
    const key = `${videoId}::level::${levelKey}`;
    const store = txStore(db, MANIFEST_STORE, 'readonly');
    const rec = await new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (rec && typeof rec.content === 'string') {
      console.log(`🎞️ [分片缓存] 命中 level m3u8 缓存: ${key}`);
      return rec.content;
    }
    return null;
  } catch (e) {
    console.warn('🎞️ [分片缓存] 读取 m3u8 缓存失败:', e);
    return null;
  }
}

export default {
  normalizeSegmentUrl,
  rewritePlaylistWithAbsoluteUrls,
  buildSegmentKey,
  buildManifestKey,
  getSegment,
  hasSegments,
  putSegment,
  putManifest,
  getManifest,
  getManifestsForVideo,
  isVideoFullyCached,
  getFullyCachedLevelKey,
  deleteVideoSegments,
  getSegmentCacheSize,
  getFullyCachedVideoIds,
  putPlaylist,
  getPlaylist
};
